import { useState, useEffect } from "react";
import { Link } from "wouter";
import { Menu, X } from "lucide-react";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useI18n } from "@/hooks/use-i18n";
import { type Lang } from "@/lib/i18n";
import { cn } from "@/lib/utils";

interface HeaderBarProps {
  lang: Lang;
  onChangeLang: (lang: Lang) => void;
  dir: "ltr" | "rtl";
}

const NAV_LINKS = [
  { id: "section-specialties", key: "nav.specialties" },
  { id: "section-journey", key: "nav.journey" },
  { id: "section-testimonials", key: "nav.testimonials" },
  { id: "section-contact", key: "nav.contact" },
] as const;

function scrollToSection(id: string) {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

export function HeaderBar({ lang, onChangeLang, dir }: HeaderBarProps) {
  const { t } = useI18n();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNavClick = (id: string) => {
    setMobileOpen(false);
    scrollToSection(id);
  };

  return (
    <header
      className={cn(
        "sticky top-0 z-50 w-full transition-all duration-500 ease-out",
        scrolled
          ? "backdrop-blur-xl bg-background/80 border-b border-border/50 shadow-sm"
          : "border-b border-border/20 bg-background/70 backdrop-blur-xl"
      )}
      dir={dir}
      data-testid="header"
    >
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between gap-4">
        <Link href="/">
          <img
            src="/images/logo_nav_hd_1x.png"
            srcSet="/images/logo_nav_hd_1x.png 1x, /images/logo_nav_hd_2x.png 2x"
            alt="Aurelian Private Care"
            className="h-10 md:h-12 w-auto"
            loading="eager"
            data-testid="link-brand"
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-8" aria-label="Main navigation">
          {NAV_LINKS.map(({ id, key }) => (
            <button
              key={id}
              type="button"
              onClick={() => scrollToSection(id)}
              className={cn(
                "relative text-sm font-medium text-foreground/90 hover:text-foreground transition-colors",
                "after:absolute after:left-0 after:bottom-[-2px] after:h-[2px] after:bg-primary after:transition-all after:duration-300",
                "after:w-0 hover:after:w-full"
              )}
            >
              {t(key)}
            </button>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-4">
          <Link href="/evaluation">
            <Button size="sm" className="shrink-0" data-testid="header-cta">
              {t("nav.cta")}
              <span className="ml-1" aria-hidden>→</span>
            </Button>
          </Link>
          <LanguageSwitcher lang={lang} onChangeLang={onChangeLang} />
        </div>

        {/* Mobile: hamburger + CTA + lang inside sheet */}
        <div className="flex md:hidden items-center gap-2">
          <Link href="/evaluation">
            <Button size="sm" variant="outline" className="shrink-0 text-xs">
              {t("nav.cta")}
            </Button>
          </Link>
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Menu">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side={dir === "rtl" ? "left" : "right"} className="w-[280px]">
              <nav className="flex flex-col gap-4 pt-8">
                {NAV_LINKS.map(({ id, key }) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => handleNavClick(id)}
                    className="text-left text-base font-medium text-foreground py-2 border-b border-border/30"
                  >
                    {t(key)}
                  </button>
                ))}
                <div className="pt-4">
                  <LanguageSwitcher lang={lang} onChangeLang={onChangeLang} />
                </div>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
